/**
 * useDuplicateAlerts — Live-Index der offenen Dedup-Warnungen einer World.
 *
 * Die neue Collection `World/{uid}/duplicateAlerts` wird ausschließlich vom
 * Backend beschrieben (DM-Finder, Research-Apply, künftig Import-Guard). Der
 * Client darf nur lesen und die vier Resolution-Felder ändern — siehe
 * `firestore.rules`.
 *
 * Design orientiert sich an HubSpot / Salesforce / Pipedrive:
 *   - Awareness: ein Zähler im Header, sobald offene Alerts existieren.
 *   - Manual review: kein Auto-Merge, immer User-Confirm.
 *   - Reversibel: "dismiss" ist wiederherstellbar, "resolved" nicht.
 *
 * Hook-API:
 *   const {
 *     openAlerts, openCount,
 *     dismissedAlerts,
 *     loading, error,
 *     dismiss, restore, markResolved,
 *     refetch,   // manuelle Neu-Ladung, sonst live
 *   } = useDuplicateAlerts(userId, { includeDismissed?: false });
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  getFirestore,
  collection,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  doc,
  updateDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { auth } from '../firebaseConfig';
import useActiveWorld from '../hooks/useActiveWorld';

const MAX_LIVE = 200; // Wenn eine World mehr als 200 offene Alerts hat, muss
                     // der User erst mal aufräumen — ein UI-Panel mit 500+
                     // Cards ist ohnehin nicht bedienbar.

export default function useDuplicateAlerts(userId, opts = {}) {
  const { includeDismissed = false } = opts;
  const db = getFirestore();

  // Permission-Guard: Die `duplicateAlerts`-Collection ist in firestore.rules
  // auf Owner + Team-Members mit `leads`-Feature beschränkt. Wenn wir das
  // Snapshot-Listener ohne diese Prüfung anhängen, wirft Firestore für Standard-
  // Members ohne `leads` (oder während eines Team-Switches, bevor die neuen
  // Features geladen sind) permanent `Missing or insufficient permissions.` in
  // die Konsole — das ist die Ursache des Warn-Spams.
  const { currentUserId, hasFeature, isSwitching } = useActiveWorld();
  const canRead = !!userId && !isSwitching && (
    currentUserId === userId || hasFeature('leads')
  );

  const [openAlerts, setOpenAlerts] = useState([]);
  const [dismissedAlerts, setDismissedAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Signature verhindert re-render bei Server-Snapshot ohne echte Änderung.
  const openSigRef = useRef('');
  const dismissedSigRef = useRef('');

  useEffect(() => {
    if (!userId || !canRead) {
      setOpenAlerts([]);
      setDismissedAlerts([]);
      openSigRef.current = '';
      dismissedSigRef.current = '';
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    setError(null);

    const col = collection(db, `World/${userId}/duplicateAlerts`);
    const openQ = query(
      col,
      where('status', '==', 'open'),
      orderBy('createdAt', 'desc'),
      limit(MAX_LIVE),
    );

    const unsubOpen = onSnapshot(
      openQ,
      (snap) => {
        const list = [];
        snap.forEach((d) => list.push({ id: d.id, ...d.data() }));
        const sig = list.map((a) => `${a.id}:${a.updatedAt?.seconds || 0}`).join('|');
        if (sig !== openSigRef.current) {
          openSigRef.current = sig;
          setOpenAlerts(list);
        }
        setLoading(false);
      },
      (err) => {
        console.warn('[useDuplicateAlerts] open snapshot failed:', err && err.message);
        setError(err);
        setLoading(false);
      },
    );

    let unsubDismissed = () => {};
    if (includeDismissed) {
      const dismissedQ = query(
        col,
        where('status', 'in', ['dismissed', 'resolved', 'merged']),
        orderBy('updatedAt', 'desc'),
        limit(100),
      );
      unsubDismissed = onSnapshot(
        dismissedQ,
        (snap) => {
          const list = [];
          snap.forEach((d) => list.push({ id: d.id, ...d.data() }));
          const sig = list.map((a) => `${a.id}:${a.updatedAt?.seconds || 0}`).join('|');
          if (sig !== dismissedSigRef.current) {
            dismissedSigRef.current = sig;
            setDismissedAlerts(list);
          }
        },
        (err) => console.warn('[useDuplicateAlerts] dismissed snapshot failed:', err && err.message),
      );
    }

    return () => { unsubOpen(); unsubDismissed(); };
  }, [db, userId, includeDismissed, canRead]);

  const patch = useCallback(async (alertId, changes) => {
    if (!userId || !alertId) return { ok: false };
    try {
      const ref = doc(db, `World/${userId}/duplicateAlerts/${alertId}`);
      const uid = auth.currentUser?.uid || null;
      await updateDoc(ref, {
        ...changes,
        resolvedBy: uid,
        resolvedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      return { ok: true };
    } catch (err) {
      console.warn('[useDuplicateAlerts] update failed:', err && err.message);
      return { ok: false, error: err };
    }
  }, [db, userId]);

  // Dismiss = "kein Duplikat" oder "später kein Bedarf". Reversibel via `restore`.
  const dismiss = useCallback((alertId, note = '') =>
    patch(alertId, { status: 'dismissed', resolvedNote: String(note).slice(0, 500) }),
  [patch]);

  // Restore = zurück auf offen. Nützlich für versehentliche Dismisses.
  const restore = useCallback((alertId) =>
    patch(alertId, { status: 'open', resolvedNote: '' }),
  [patch]);

  // markResolved = "wurde gemerged" bzw. anderweitig behandelt. Nicht reversibel.
  const markResolved = useCallback((alertId, mergeInfo = null) =>
    patch(alertId, {
      status: mergeInfo ? 'merged' : 'resolved',
      resolvedNote: mergeInfo ? `merged into ${mergeInfo.winnerId}` : '',
    }),
  [patch]);

  return {
    openAlerts,
    openCount: openAlerts.length,
    dismissedAlerts,
    loading,
    error,
    dismiss,
    restore,
    markResolved,
  };
}
